import React, {memo} from 'react';
import {Button} from '@tickchak/react-share/app';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import * as css from './GeneratorChatMessage.css';
import {AssistantChatMessage, ChatMessage} from '../utils/types';
import {formatDraftMessage} from '../formatDraftMessage/formatDraftMessage';
import {useVariables} from '../../../assets/objects/Variables';
import {TState} from '../../../redux/store.type';

export interface GeneratorChatMessageProps {
    message: ChatMessage;
    index: number;
    messages: ChatMessage[];
    draftLoading: boolean;
    approveLabel: string;
    approveLoading: boolean;
    onApprove: (draft: AssistantChatMessage) => void | Promise<void>;
}

function GeneratorChatMessage({
    message,
    index,
    messages,
    draftLoading,
    approveLabel,
    approveLoading,
    onApprove,
}: GeneratorChatMessageProps) {
    const {defaultVariables} = useVariables();
    const {t} = useTranslation('variables');
    const currentLang = useSelector((s: TState) => s.branding.currentLang);

    if(message.role === 'user') {
        return (
            <css.userBubble data-testid={`ai-message-generator-user-${index}`}>
                {message.text}
            </css.userBubble>
        );
    }

    const assistantIndex = messages
        .slice(0, index)
        .filter((item): item is AssistantChatMessage => item.role === 'assistant')
        .length;
    const hasApprovalContent = Boolean(message.text?.trim()) || Boolean(message.parameterValues);

    return (
        <css.aiCard data-testid={`ai-message-generator-assistant-${assistantIndex}`}>
            <css.aiCardBody data-testid="ai-message-generator-draft">
                {formatDraftMessage(message.text, defaultVariables, t, message.parameterValues, currentLang)}
            </css.aiCardBody>
            <css.aiCardDivider />
            <css.aiCardActions>
                <Button
                    variant="brand"
                    size="category"
                    disabled={approveLoading || draftLoading || !hasApprovalContent}
                    onClick={() => void onApprove(message)}
                    data-testid={`ai-message-generator-approve-${assistantIndex}`}
                >
                    {approveLabel}
                </Button>
            </css.aiCardActions>
        </css.aiCard>
    );
}

export default memo(GeneratorChatMessage);
